import type { SegmentUrlResolver } from '@mimir/client-common/util/segmentUrlCache';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { BufferFetching, FragmentedMp4Provider } from './fetching';
import { soloAudioStream } from '@mimir/common/mpegts/soloAudioStream';
import { TransmuxUtility, type FlacDownmixConfig } from '@mimir/common/mpegts/transmuxUtility';
import type { AudioTransform, SoloAudioTrack } from './audioTransform';
import { invariant } from '@mimir/common/util/invariant';
import cloneDeep from 'lodash/cloneDeep';
import type { ObservableState } from '@mimir/common/util/observable';
import { Lock } from '@mimir/common/async/lock';
import type { FragmentedMp4Buffers } from '@mimir/common/mp4/fragmentedMp4Types';


export function awsMediaLiveArchiveSegmentSuffixNaming(segmentTime: number) {
  return '.' + segmentTime.toString().padStart(6, '0') + '.ts';
}

export class Mpeg2TransportStreamRemuxer implements FragmentedMp4Provider {

  transmuxer: TransmuxUtility | null = null;

  currentAudioTransform: AudioTransform;

  lastDecodedSegment = -1;

  constructor(
    private urlResolver: SegmentUrlResolver,
    private fetching: BufferFetching,
    readonly itemId: string,
    private audioTrackCount: ObservableState<number | null>,
    private useFlacDownmix: boolean,
    private playerId: string,
  ) {
    this.currentAudioTransform = useFlacDownmix
      ? {
        type: 'flacDownmix',
        mixMatrix: [],
      }
      : {
        type: 'solo',
        audioTrackIndex: 0,
      };
  }

  private getFlacDownmixConfig(audioTransform: AudioTransform): FlacDownmixConfig | null {
    if (audioTransform.type === 'solo') {
      return null;
    }

    return {
      playerId: this.playerId,
      mixMatrix: audioTransform.mixMatrix === null ? [] : cloneDeep(audioTransform.mixMatrix),
      audioChannelCount: this.audioTrackCount,
    };
  }

  private async makeInitializedTransmuxer(streamId: StreamRoleId, audioTransform: AudioTransform) {
    const buf0 = await this.getBuffer(streamId, 0);
    const tsBytes0 = (new Uint8Array(buf0.buffer)).slice();

    if (!this.useFlacDownmix) {
      invariant(audioTransform.type === 'solo', 'Only soloing is supporting when flacDownmix is not set');
      soloAudioStream(tsBytes0, audioTransform.audioTrackIndex);
    }

    const flacDownmixConfig = this.getFlacDownmixConfig(audioTransform);
    const transmuxer = new TransmuxUtility();
    /*
      Add segment zero first, in order to initialize the right timestamp offset.
    */
    try {
      transmuxer.transmuxSegment(tsBytes0, { url: buf0.url }, flacDownmixConfig);
      this.lastDecodedSegment = 0;
    } catch(err) {
      // If anything bad happens in the transmuxer, we need to print it.
      // Otherwise, debugging issues is next to impossible
      console.error('transmuxer.transmuxSegment error', err);
    }

    return transmuxer;
  }


  private audioTransformChangeRequiresNewTransmuxer(audioTransform: AudioTransform) {
    if (this.currentAudioTransform.type !== audioTransform.type) {
      return true;
    }
    if (this.currentAudioTransform.type === 'solo') {
      // If soloing selects a different audio stream that the current one, we need
      // to reinitialize the transmuxer, because the stream ID will be different
      // and the media source extension does not like this
      return this.currentAudioTransform.audioTrackIndex !== (audioTransform as SoloAudioTrack).audioTrackIndex;
    }
    // For Flac downmix, we always use the same audio layout, so there is no need to re-initialize
    return false;
  }


  private async getTransmuxer(streamId: StreamRoleId, audioTransform: AudioTransform) {
    if (this.audioTransformChangeRequiresNewTransmuxer(audioTransform)) {
      this.transmuxer = null;
    }
    if (!this.transmuxer) {
      this.transmuxer = await this.makeInitializedTransmuxer(streamId, audioTransform);
      this.currentAudioTransform = audioTransform;
    }
    return this.transmuxer;
  }

  private async getBuffer(streamId: StreamRoleId, segmentTime: number) {
    const url = await this.urlResolver.getMediaSegmentUrl(this.itemId, streamId, segmentTime);
    return {
      buffer: await this.fetching.getBuffer(url),
      url: url
    };
  }

  private transmuxerLock = new Lock();

  async getMediaSegmentBuffers(needInitialization: boolean, streamId: StreamRoleId, segmentTime: number, audioTransform: AudioTransform): Promise<FragmentedMp4Buffers> {
    invariant(audioTransform.type !== 'flacDownmix' || this.useFlacDownmix, 'FlacDownmix transform not supported when flacDownmix flag is not set');
    const previousSegmentTime = Math.max(0, segmentTime - 1);
    const [previousBuf, bufN] = await Promise.all([
      this.getBuffer(streamId, previousSegmentTime),
      this.getBuffer(streamId, segmentTime),
    ]);
    let tsBytes = (new Uint8Array(bufN.buffer)).slice();

    if (audioTransform.type === 'solo') {
      const audioTrackCount = soloAudioStream(tsBytes, audioTransform.audioTrackIndex);
      this.audioTrackCount.value = audioTrackCount;
    }

    if (this.useFlacDownmix) {
      // We don't want to solo when using flac downmix
      tsBytes = new Uint8Array(bufN.buffer);
    }

    const flacDownmixConfig = this.getFlacDownmixConfig(audioTransform);

    try {
      // Since the correctness of Flac downmixing depends on keeping some internal state
      // between decoding segment N-1 and segment N (to get clean audio without
      // click artifacts at segment boundaries), we use a basic lock to ensure
      // that when AAC initialization is needed, it is done immediately before
      // decoding segment N without any other decoding operation occurring in between
      await this.transmuxerLock.waitForLock();

      const transmuxer = await this.getTransmuxer(streamId, audioTransform);
      if (this.useFlacDownmix) {
        if (previousSegmentTime !== this.lastDecodedSegment) {
          // If the last decoded segment is not the one immediately before
          // the segment N want to decode now, we need to re-initialize the AAC
          // decoders with the last AAC data from the segment N-1 in order
          // to avoid audio glitches at segment boundary. Setting onlyUseLastAacPacket
          // to true allows us to avoid decoding all AAC packets except the last one
          // in each audio stream
          const onlyUseLastAacPacket = true;
          const previousBytes = (new Uint8Array(previousBuf.buffer)).slice();
          transmuxer.transmuxSegment(previousBytes, { url: bufN.url, segmentTime: previousSegmentTime }, flacDownmixConfig, onlyUseLastAacPacket);
        }
      }
      const r = transmuxer.transmuxSegment(tsBytes, { url: bufN.url, segmentTime }, flacDownmixConfig);
      this.lastDecodedSegment = segmentTime;

      return r;
    } catch (err) {
      console.error('Transmux error. Trying to recover', err);
    } finally {
      this.transmuxerLock.releaseLock();
    }

    this.transmuxer = null;

    try {
      await this.transmuxerLock.waitForLock();
      const transmuxer2 = await this.getTransmuxer(streamId, audioTransform);
      const res = transmuxer2.transmuxSegment(tsBytes, { url: bufN.url, segmentTime }, flacDownmixConfig);
      this.lastDecodedSegment = segmentTime;
      return res;
    } finally {
      this.transmuxerLock.releaseLock();
    }
  }

  cleanup() {
  }
}
